// Stock management — Phase 1 (2026-06-10), UI/UX rework (2026-06-15).
//
// Perpetual inventory for the atelier: matières premières (with purchase→consume
// yield + moving-average cost), the album recette (BOM), achats, and the
// append-only movements ledger. Auto-deduction happens worker-side when an order
// reaches 'printed' (Imprimée) — this page is where the team SEES and corrects
// stock.
//
// 2026-06-15 rework — three clearly-labeled sections, mobile-first card rows,
// warm/gold Storiz editorial palette (COLORS / brand.ts), Fraunces section
// titles. Behaviour is unchanged: every modal + mutation is the same wiring,
// only the presentation is restructured.
//
// Design language: admin redesign (clusters, gold accents, Fraunces headings,
// adm-rise). All modals PORTALED to body (adm-rise transform gotcha).

import { useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { stockApi, type Material, type BomLine, type OrderProfit } from '@/lib/api';
import { ADM_COLORS as COLORS, withAlpha } from '@/lib/brand';
import { BASE_PAGES_PER_ALBUM } from '@/lib/orderPricing';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/Button';
import { ReceiptIntakeModal } from '@/components/admin/ReceiptIntakeModal';
import { useT } from '@/lib/i18n';

const GOLD = COLORS.accentPrimary;       // #C9A04C
const GOLD_DARK = 'var(--color-adm-gold-ink)'; // #8B6F26 in light
const SERIF = "'Fraunces', Georgia, serif";

/** Canonical base for the true per-album cost model — the page allowance a NEW
 *  order is sold with (see BASE_PAGES_PER_ALBUM in lib/orderPricing.ts). This
 *  page simulates the cost of the album we sell TODAY; it is not tied to any
 *  one order, so the current default is the right figure here. */
const BASE_PAGES = BASE_PAGES_PER_ALBUM;

const fmtQty = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, ''));
const fmtMad = (n: number) => `${(Math.round(n * 100) / 100).toLocaleString('fr-FR')} MAD`;

/** 'album' lines feed the album cost model; 'business' are general supplies. */
const classOf = (m: Pick<Material, 'material_class'>): 'album' | 'business' => m.material_class ?? 'album';

// ─── Section heading — Fraunces serif, gold rule, optional badge/action ───────
function SectionHead({ title, subtitle, badge, action }: {
  title: string; subtitle?: string; badge?: React.ReactNode; action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 flex-wrap mb-4">
      <div className="min-w-0">
        <h2 className="text-[20px] leading-tight font-semibold" style={{ fontFamily: SERIF, color: COLORS.textPrimary }}>
          {title}
        </h2>
        {subtitle && <p className="text-[12.5px] mt-0.5" style={{ color: COLORS.textSecondary }}>{subtitle}</p>}
      </div>
      {badge}
      {action && <div className="ml-auto">{action}</div>}
    </div>
  );
}

/** Album / business tag pill — album = gold accent, business = neutral. */
function ClassTag({ value }: { value: 'album' | 'business' }) {
  const { t } = useT();
  return value === 'album'
    ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: COLORS.accentSoft, color: GOLD_DARK, border: `1px solid ${withAlpha(GOLD, '55')}` }}>{t('Album')}</span>
    : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: COLORS.bgElevated, color: COLORS.textSecondary, border: `1px solid ${COLORS.borderSubtle}` }}>{t('Atelier')}</span>;
}

// ─── Shared modal shell (portaled — never trapped by transformed ancestors) ──
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  const { t } = useT();
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(46,36,24,0.18)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[520px] max-h-[88vh] overflow-y-auto rounded-2xl p-5 adm-rise"
        style={{ background: COLORS.bgCard, boxShadow: '0 8px 24px rgba(46,36,24,0.18)', border: `1px solid ${COLORS.borderSubtle}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 mb-4">
          <h3 className="text-[16px] font-semibold" style={{ fontFamily: SERIF, color: COLORS.textPrimary }}>{title}</h3>
          <button
            type="button" onClick={onClose} aria-label={t('Fermer')}
            className="ml-auto w-11 h-11 rounded-lg flex items-center justify-center"
            style={{ border: `1px solid ${COLORS.borderSubtle}`, color: COLORS.textTertiary }}
          >✕</button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{label}</label>
      {children}
      {hint && <p className="text-[11px] mt-0.5" style={{ color: COLORS.textTertiary }}>{hint}</p>}
    </div>
  );
}

const inputCls = 'w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white';
const inputStyle = { borderColor: COLORS.borderSubtle, color: COLORS.textPrimary } as const;

// ─── Cutting calculator (2026-06-10) ─────────────────────────────────────────
// The album build spec, in mm — each piece the atelier cuts from purchased
// sheets. The yield uses simple guillotine math (both orientations); the
// admin always sees and can override the suggested units_per_purchase.
const PIECE_PRESETS: Record<string, { label: string; w: number; h: number; perAlbumNote?: string }> = {
  planche:    { label: 'Planche (avant/arrière) 216×303', w: 216, h: 303 },
  dos:        { label: 'Dos 10×303', w: 10, h: 303 },
  kit_album:  { label: 'Kit album (4 planches doublées + 2 dos)', w: 216, h: 303, perAlbumNote: 'dos pris dans les chutes' },
  vinyle:     { label: 'Vinyle couverture 483×329', w: 483, h: 329 },
  feuille_a3: { label: 'Feuille intérieur A3 297×420', w: 297, h: 420 },
  page_garde: { label: 'Page de garde A3 420×297', w: 420, h: 297 },
  custom:     { label: 'Personnalisé…', w: 0, h: 0 },
};

/** Pieces per sheet, guillotine cut, best of both orientations. */
function fitPieces(W: number, H: number, w: number, h: number): number {
  if (!(W > 0 && H > 0 && w > 0 && h > 0)) return 0;
  const a = Math.floor(W / w) * Math.floor(H / h);
  const b = Math.floor(W / h) * Math.floor(H / w);
  return Math.max(a, b);
}

/** Yield for a piece kind: kit_album = floor(planches / 4) — boards doubled.
 *  Returns raw fields (kind/pieces/w/h) so the caller can build a translated
 *  `detail` string via t(); the French `detail` here is kept as a fallback. */
function computeYield(kind: string, W: number, H: number, cw: number, ch: number): { units: number; detail: string; kind: string; pieces: number; w: number; h: number } {
  const preset = PIECE_PRESETS[kind];
  if (!preset) return { units: 0, detail: '', kind, pieces: 0, w: 0, h: 0 };
  const w = kind === 'custom' ? cw : preset.w;
  const h = kind === 'custom' ? ch : preset.h;
  const pieces = fitPieces(W, H, w, h);
  if (kind === 'kit_album') {
    // Each board in the book is DOUBLED (2 plies glued) → 4 pieces 216×303
    // per album (front ×2 + back ×2); the doubled spines come from offcuts.
    const kits = Math.floor(pieces / 4);
    return { units: kits, detail: `${pieces} planches → ${kits} kit${kits > 1 ? 's' : ''} (planches doublées ×2, dos dans les chutes)`, kind, pieces, w, h };
  }
  return { units: pieces, detail: `${pieces} pièce${pieces > 1 ? 's' : ''} de ${w}×${h} mm`, kind, pieces, w, h };
}

// ─── Material create/edit modal ──────────────────────────────────────────────
function MaterialModal({ material, onClose }: { material: Material | null; onClose: () => void }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: material?.name ?? '',
    category: material?.category ?? 'consommable',
    material_class: (material?.material_class ?? 'album') as 'album' | 'business',
    purchase_unit: material?.purchase_unit ?? 'unité',
    consume_unit: material?.consume_unit ?? 'unité',
    units_per_purchase: material?.units_per_purchase ?? 1,
    avg_cost: material?.avg_cost ?? 0,
    min_qty: material?.min_qty ?? ('' as number | ''),
    max_qty: material?.max_qty ?? ('' as number | ''),
    lead_time_days: material?.lead_time_days ?? ('' as number | ''),
    notes: material?.notes ?? '',
    purchase_w_mm: material?.purchase_w_mm ?? ('' as number | ''),
    purchase_h_mm: material?.purchase_h_mm ?? ('' as number | ''),
    piece_kind: material?.piece_kind ?? ('' as string),
    piece_w_mm: material?.piece_w_mm ?? ('' as number | ''),
    piece_h_mm: material?.piece_h_mm ?? ('' as number | ''),
  });
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  // ✨ AI fill — describe the material in plain words, Gemini pre-fills the
  // form. The admin reviews everything; nothing saves automatically.
  const [aiText, setAiText] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiPriceHint, setAiPriceHint] = useState<number | null>(null);
  const runAi = async () => {
    if (!aiText.trim()) return;
    setAiBusy(true); setAiError(null);
    try {
      const { suggestion: s } = await stockApi.aiMaterial(aiText.trim());
      setForm((f) => ({
        ...f,
        name: s.name ?? f.name,
        category: (s.category as 'consommable' | 'fourniture') ?? f.category,
        purchase_unit: s.purchase_unit ?? f.purchase_unit,
        consume_unit: s.consume_unit ?? f.consume_unit,
        min_qty: s.min_qty ?? f.min_qty,
        lead_time_days: s.lead_time_days ?? f.lead_time_days,
        notes: s.notes ?? f.notes,
        purchase_w_mm: s.purchase_w_mm ?? f.purchase_w_mm,
        purchase_h_mm: s.purchase_h_mm ?? f.purchase_h_mm,
        piece_kind: (s.piece_kind as string) ?? f.piece_kind,
        piece_w_mm: s.piece_w_mm ?? f.piece_w_mm,
        piece_h_mm: s.piece_h_mm ?? f.piece_h_mm,
      }));
      setAiPriceHint(s.price_per_purchase_unit ?? null);
    } catch (e) {
      setAiError((e as Error).message);
    } finally {
      setAiBusy(false);
    }
  };

  // Delete (edit mode) — wrong entries. Worker falls back to deactivation
  // when real purchases reference the material.
  const [delInfo, setDelInfo] = useState<string | null>(null);
  const del = useMutation({
    mutationFn: () => stockApi.deleteMaterial(material!.id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['stock-materials'] });
      queryClient.invalidateQueries({ queryKey: ['stock-bom'] });
      queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      if (res.deactivated) setDelInfo(res.reason ?? t('Article désactivé.'));
      else onClose();
    },
  });

  // Live yield from the calculator inputs.
  const calc = form.piece_kind
    ? computeYield(form.piece_kind, Number(form.purchase_w_mm) || 0, Number(form.purchase_h_mm) || 0, Number(form.piece_w_mm) || 0, Number(form.piece_h_mm) || 0)
    : null;

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        units_per_purchase: Number(form.units_per_purchase) || 1,
        avg_cost: Number(form.avg_cost) || 0,
        min_qty: form.min_qty === '' ? null : Number(form.min_qty),
        max_qty: form.max_qty === '' ? null : Number(form.max_qty),
        lead_time_days: form.lead_time_days === '' ? null : Number(form.lead_time_days),
        purchase_w_mm: form.purchase_w_mm === '' ? null : Number(form.purchase_w_mm),
        purchase_h_mm: form.purchase_h_mm === '' ? null : Number(form.purchase_h_mm),
        piece_kind: form.piece_kind || null,
        piece_w_mm: form.piece_w_mm === '' ? null : Number(form.piece_w_mm),
        piece_h_mm: form.piece_h_mm === '' ? null : Number(form.piece_h_mm),
      };
      return material
        ? stockApi.updateMaterial(material.id, payload as Partial<Material>)
        : stockApi.createMaterial(payload as Partial<Material>);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-materials'] });
      queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['stock-bom'] });
      onClose();
    },
  });

  return (
    <Modal title={material ? t('Modifier — {name}', { name: material.name }) : t('Nouvel article')} onClose={onClose}>
      <div className="space-y-3">
        {/* ✨ AI fill — also in edit mode, to CORRECT a wrong entry */}
        {(
          <div className="rounded-xl p-3 space-y-2" style={{ background: 'rgba(201,160,76,.07)', border: `1px dashed ${withAlpha(GOLD, '66')}` }}>
            <p className="text-[11px] font-semibold" style={{ color: GOLD_DARK }}>✨ {material ? t('Décrivez le matériau (ou la correction) — l’IA remplit le formulaire (vous validez)') : t('Décrivez le matériau — l’IA remplit le formulaire (vous validez)')}</p>
            <div className="flex gap-2">
              <input
                className={inputCls} style={inputStyle}
                value={aiText}
                onChange={(e) => setAiText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runAi(); }}
                placeholder={t('ex. « plaque carton gris 2mm 100×80cm à 30dh pour les planches d’album »')}
              />
              <Button size="sm" onClick={runAi} loading={aiBusy} disabled={!aiText.trim()}>{t('Remplir')}</Button>
            </div>
            {aiError && <p className="text-[11px]" style={{ color: COLORS.error }}>{aiError}</p>}
            {aiPriceHint != null && (
              <p className="text-[11px]" style={{ color: GOLD_DARK }}>
                💡 {t('Prix détecté :')} {aiPriceHint} MAD / {form.purchase_unit || t('unité')} — {t('saisissez-le lors du premier')} <b>{t('achat')}</b> {t('(le coût moyen suit les achats réels).')}
              </p>
            )}
          </div>
        )}

        <Field label={t('Nom de l’article')}>
          <input className={inputCls} style={inputStyle} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder={t('Plaque rigide, carton, colle…')} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('Classe')} hint={t('Album = compte dans le coût d’album ; Atelier = fourniture générale')}>
            <select className={inputCls} style={inputStyle} value={form.material_class} onChange={(e) => set('material_class', e.target.value)}>
              <option value="album">{t('Matière d’album')}</option>
              <option value="business">{t('Fourniture atelier')}</option>
            </select>
          </Field>
          <Field label={t('Type')} hint={t('Consommable = déduit par album (recette)')}>
            <select className={inputCls} style={inputStyle} value={form.category} onChange={(e) => set('category', e.target.value)}>
              <option value="consommable">{t('Consommable d’album')}</option>
              <option value="fourniture">{t('Fourniture / outil')}</option>
            </select>
          </Field>
        </div>
        <Field label={t('Délai de réappro (jours)')}>
          <input className={inputCls} style={inputStyle} type="number" min="0" value={form.lead_time_days} onChange={(e) => set('lead_time_days', e.target.value)} placeholder={t('ex. 3')} />
        </Field>
        <div className="rounded-xl p-3 space-y-3" style={{ background: 'rgba(201,160,76,.06)', border: `1px solid ${withAlpha(GOLD, '40')}` }}>
          <p className="text-[11px] font-semibold" style={{ color: GOLD_DARK }}>{t('Découpe — comment on achète vs comment on consomme')}</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('Unité d’achat')} hint={t('ex. plaque 100×80, pot, boîte')}>
              <input className={inputCls} style={inputStyle} value={form.purchase_unit} onChange={(e) => set('purchase_unit', e.target.value)} />
            </Field>
            <Field label={t('Unité de consommation')} hint={t('ex. kit album, dose, carton')}>
              <input className={inputCls} style={inputStyle} value={form.consume_unit} onChange={(e) => set('consume_unit', e.target.value)} />
            </Field>
          </div>
          {/* Cutting calculator — sheet size + spec piece → yield */}
          <div className="rounded-lg p-2.5 space-y-2 bg-white/70" style={{ border: `1px solid ${COLORS.borderSubtle}` }}>
            <p className="text-[11px] font-semibold" style={{ color: COLORS.textSecondary }}>📐 {t('Calculateur — entrez la taille de la plaque achetée, on calcule le rendement')}</p>
            <div className="grid grid-cols-3 gap-2">
              <Field label={t('Largeur (mm)')}>
                <input className={inputCls} style={inputStyle} type="number" min="0" value={form.purchase_w_mm} onChange={(e) => set('purchase_w_mm', e.target.value)} placeholder="1000" />
              </Field>
              <Field label={t('Hauteur (mm)')}>
                <input className={inputCls} style={inputStyle} type="number" min="0" value={form.purchase_h_mm} onChange={(e) => set('purchase_h_mm', e.target.value)} placeholder="800" />
              </Field>
              <Field label={t('Pièce découpée')}>
                <select className={inputCls} style={inputStyle} value={form.piece_kind} onChange={(e) => set('piece_kind', e.target.value)}>
                  <option value="">—</option>
                  {Object.entries(PIECE_PRESETS).map(([k, p]) => <option key={k} value={k}>{t(p.label)}</option>)}
                </select>
              </Field>
            </div>
            {form.piece_kind === 'custom' && (
              <div className="grid grid-cols-2 gap-2">
                <Field label={t('Largeur pièce (mm)')}>
                  <input className={inputCls} style={inputStyle} type="number" min="0" value={form.piece_w_mm} onChange={(e) => set('piece_w_mm', e.target.value)} />
                </Field>
                <Field label={t('Hauteur pièce (mm)')}>
                  <input className={inputCls} style={inputStyle} type="number" min="0" value={form.piece_h_mm} onChange={(e) => set('piece_h_mm', e.target.value)} />
                </Field>
              </div>
            )}
            {calc && calc.units > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold" style={{ color: GOLD_DARK }}>→ {calc.kind === 'kit_album'
                  ? t('{p} planches → {k} kit(s) (planches doublées ×2, dos dans les chutes)', { p: calc.pieces, k: calc.units })
                  : t('{p} pièce(s) de {w}×{h} mm', { p: calc.pieces, w: calc.w, h: calc.h })}</span>
                {Number(form.units_per_purchase) !== calc.units && (
                  <button
                    type="button"
                    onClick={() => set('units_per_purchase', calc.units)}
                    className="text-[11px] font-bold px-2.5 py-1 rounded-lg text-white"
                    style={{ background: `linear-gradient(135deg, ${GOLD}, #A8842F)` }}
                  >
                    {t('Utiliser :')} {calc.units} / {form.purchase_unit || t('plaque')}
                  </button>
                )}
              </div>
            )}
          </div>

          <Field label={t('1 {pu} = combien de {cu} ?', { pu: form.purchase_unit || t('unité d’achat'), cu: form.consume_unit || t('unités') })} hint={t('Le rendement de découpe — calculé ci-dessus ou saisi à la main')}>
            <input className={inputCls} style={inputStyle} type="number" min="0.01" step="0.01" value={form.units_per_purchase} onChange={(e) => set('units_per_purchase', e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label={t('Coût moyen')} hint={`MAD / ${form.consume_unit || t('unité')}`}>
            <input className={inputCls} style={inputStyle} type="number" min="0" step="0.01" value={form.avg_cost} onChange={(e) => set('avg_cost', e.target.value)} />
          </Field>
          <Field label={t('Stock min')} hint={t('Alerte en-dessous')}>
            <input className={inputCls} style={inputStyle} type="number" min="0" value={form.min_qty} onChange={(e) => set('min_qty', e.target.value)} />
          </Field>
          <Field label={t('Stock max')}>
            <input className={inputCls} style={inputStyle} type="number" min="0" value={form.max_qty} onChange={(e) => set('max_qty', e.target.value)} />
          </Field>
        </div>
        <Field label={t('Notes')}>
          <input className={inputCls} style={inputStyle} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder={t('Fournisseur, référence…')} />
        </Field>
        <div className="flex items-center gap-2 pt-1">
          {material && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm(t('Supprimer « {name} » ? Les mouvements et la ligne de recette liés seront supprimés aussi.', { name: material.name }))) del.mutate();
              }}
              className="px-3 py-2.5 rounded-xl text-xs font-semibold"
              style={{ color: COLORS.error, border: `1px solid ${withAlpha(COLORS.error, '44')}` }}
            >
              {del.isPending ? '…' : t('Supprimer')}
            </button>
          )}
          <div className="flex-1" />
          <Button variant="ghost" size="sm" onClick={onClose}>{t('Annuler')}</Button>
          <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!form.name.trim()}>
            {material ? t('Enregistrer') : t('Créer l’article')}
          </Button>
        </div>
        {save.isError && <p className="text-xs" style={{ color: COLORS.error }}>{(save.error as Error).message}</p>}
        {del.isError && <p className="text-xs" style={{ color: COLORS.error }}>{(del.error as Error).message}</p>}
        {delInfo && <p className="text-xs" style={{ color: COLORS.warning }}>{delInfo}</p>}
      </div>
    </Modal>
  );
}

// ─── Manual adjust modal ──────────────────────────────────────────────────────
function AdjustModal({ material, onClose }: { material: Material; onClose: () => void }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [qty, setQty] = useState('');
  const [direction, setDirection] = useState<'+' | '-'>('+');
  const [reason, setReason] = useState<'ajustement' | 'perte'>('ajustement');
  const [note, setNote] = useState('');

  const save = useMutation({
    mutationFn: () => stockApi.adjust({
      material_id: material.id,
      qty: (direction === '+' ? 1 : -1) * Math.abs(Number(qty)),
      reason,
      note: note || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-materials'] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
      onClose();
    },
  });

  return (
    <Modal title={t('Ajuster — {name}', { name: material.name })} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs" style={{ color: COLORS.textSecondary }}>
          {t('Stock actuel :')} <b style={{ color: COLORS.textPrimary }}>{fmtQty(material.stock_qty)} {material.consume_unit}</b>
        </p>
        <div className="flex gap-2">
          {(['+', '-'] as const).map((d) => (
            <button
              key={d} type="button" onClick={() => setDirection(d)}
              className="flex-1 py-3 rounded-xl text-sm font-bold border transition-colors"
              style={direction === d
                ? { background: d === '+' ? withAlpha(COLORS.success, 0.14) : withAlpha(COLORS.error, 0.12), borderColor: d === '+' ? COLORS.success : COLORS.error, color: d === '+' ? COLORS.success : COLORS.error }
                : { borderColor: COLORS.borderSubtle, color: COLORS.textTertiary }}
            >
              {d === '+' ? `+ ${t('Entrée')}` : `− ${t('Sortie')}`}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('Quantité ({unit})', { unit: material.consume_unit })}>
            <input className={inputCls} style={inputStyle} type="number" min="0.01" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} autoFocus />
          </Field>
          <Field label={t('Raison')}>
            <select className={inputCls} style={inputStyle} value={reason} onChange={(e) => setReason(e.target.value as 'ajustement' | 'perte')}>
              <option value="ajustement">{t('Ajustement (comptage)')}</option>
              <option value="perte">{t('Perte / chute')}</option>
            </select>
          </Field>
        </div>
        <Field label={t('Note (optionnel)')}>
          <input className={inputCls} style={inputStyle} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('ex. inventaire du 10/06')} />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>{t('Annuler')}</Button>
          <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!(Number(qty) > 0)}>{t('Valider')}</Button>
        </div>
        {save.isError && <p className="text-xs" style={{ color: COLORS.error }}>{(save.error as Error).message}</p>}
      </div>
    </Modal>
  );
}

// ─── Purchase modal ───────────────────────────────────────────────────────────
function PurchaseModal({ materials, onClose }: { materials: Material[]; onClose: () => void }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [supplier, setSupplier] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<Array<{ material_id: string; qty: string; price: string }>>([
    { material_id: materials[0]?.id ?? '', qty: '', price: '' },
  ]);

  const setLine = (i: number, k: string, v: string) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const total = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.price) || 0), 0);
  const valid = lines.every((l) => l.material_id && Number(l.qty) > 0 && Number(l.price) >= 0) && lines.length > 0;

  const save = useMutation({
    mutationFn: () => stockApi.createPurchase({
      supplier: supplier || undefined,
      note: note || undefined,
      lines: lines.map((l) => ({ material_id: l.material_id, qty_purchase_units: Number(l.qty), unit_price: Number(l.price) })),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-materials'] });
      queryClient.invalidateQueries({ queryKey: ['stock-purchases'] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
      onClose();
    },
  });

  return (
    <Modal title={t('Nouvel achat')} onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('Fournisseur (optionnel)')}>
            <input className={inputCls} style={inputStyle} value={supplier} onChange={(e) => setSupplier(e.target.value)} />
          </Field>
          <Field label={t('Note')}>
            <input className={inputCls} style={inputStyle} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>

        <div className="space-y-2">
          {lines.map((l, i) => {
            const mat = materials.find((m) => m.id === l.material_id);
            return (
              <div key={i} className="rounded-xl p-3 space-y-2" style={{ border: `1px solid ${COLORS.borderSubtle}` }}>
                <div className="flex items-center gap-2">
                  <select className={inputCls} style={inputStyle} value={l.material_id} onChange={(e) => setLine(i, 'material_id', e.target.value)}>
                    {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  {lines.length > 1 && (
                    <button type="button" className="text-sm px-2" style={{ color: COLORS.error }} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>✕</button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label={t('Quantité ({unit})', { unit: mat?.purchase_unit ?? t('unité') })}>
                    <input className={inputCls} style={inputStyle} type="number" min="0.01" step="0.01" value={l.qty} onChange={(e) => setLine(i, 'qty', e.target.value)} />
                  </Field>
                  <Field label={t('Prix / {unit} (MAD)', { unit: mat?.purchase_unit ?? t('unité') })}>
                    <input className={inputCls} style={inputStyle} type="number" min="0" step="0.01" value={l.price} onChange={(e) => setLine(i, 'price', e.target.value)} />
                  </Field>
                </div>
                {mat && Number(l.qty) > 0 && (
                  <p className="text-[11px]" style={{ color: GOLD_DARK }}>
                    → {t('entrée de')} {fmtQty(Number(l.qty) * mat.units_per_purchase)} {mat.consume_unit}
                    {Number(l.price) > 0 && <> {t('à')} {fmtMad(Number(l.price) / mat.units_per_purchase)} / {mat.consume_unit}</>}
                  </p>
                )}
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => setLines((ls) => [...ls, { material_id: materials[0]?.id ?? '', qty: '', price: '' }])}
            className="text-xs font-semibold" style={{ color: GOLD_DARK }}
          >
            + {t('Ajouter une ligne')}
          </button>
        </div>

        <div className="flex items-center justify-between pt-1">
          <p className="text-sm font-bold" style={{ color: COLORS.textPrimary }}>{t('Total :')} {fmtMad(total)}</p>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>{t('Annuler')}</Button>
            <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!valid}>{t('Valider l’achat')}</Button>
          </div>
        </div>
        {save.isError && <p className="text-xs" style={{ color: COLORS.error }}>{(save.error as Error).message}</p>}
      </div>
    </Modal>
  );
}

// ─── Section 3: Recette album (live true-cost BOM @ BASE_PAGES) ──────────────────────────────
function RecipeCard({ materials }: { materials: Material[] }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['stock-bom'], queryFn: () => stockApi.bom() });
  const [lines, setLines] = useState<Array<{ material_id: string; qty_fixed: string; qty_per_page: string }> | null>(null);
  const [simPages, setSimPages] = useState(BASE_PAGES);

  const serverLines = useMemo(
    () => (data?.bom ?? []).map((l: BomLine) => ({ material_id: l.material_id, qty_fixed: String(l.qty_fixed), qty_per_page: String(l.qty_per_page) })),
    [data],
  );
  const current = lines ?? serverLines;
  const dirty = lines != null && JSON.stringify(lines) !== JSON.stringify(serverLines);

  const save = useMutation({
    mutationFn: () => stockApi.saveBom(current.map((l) => ({ material_id: l.material_id, qty_fixed: Number(l.qty_fixed) || 0, qty_per_page: Number(l.qty_per_page) || 0 }))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-bom'] });
      setLines(null);
    },
  });

  const matFor = (id: string) => materials.find((m) => m.id === id);
  // Only album-class consumables belong in the album cost model.
  const consumables = materials.filter((m) => m.category === 'consommable' && m.active && classOf(m) === 'album');
  const available = consumables.filter((m) => !current.some((l) => l.material_id === m.id));

  const lineCost = (l: { material_id: string; qty_fixed: string; qty_per_page: string }) => {
    const m = matFor(l.material_id);
    if (!m || classOf(m) !== 'album') return 0;
    const qty = (Number(l.qty_fixed) || 0) + (Number(l.qty_per_page) || 0) * simPages;
    return qty * m.avg_cost;
  };
  const costPerAlbum = current.reduce((s, l) => s + lineCost(l), 0);

  return (
    <div className="rounded-2xl border p-5" style={{ background: COLORS.bgCard, borderColor: COLORS.borderSubtle, boxShadow: '0 2px 8px rgba(46,36,24,0.05)' }}>
      <SectionHead
        title={t('Recette d’un album')}
        subtitle={t('Coût matières réel, à {n} pages. Chaque album consomme : quantité fixe + quantité × pages. À l’« Imprimée », chaque commande déduit automatiquement (recette × nb d’albums).', { n: BASE_PAGES })}
      />

      {/* Prominent total — gold hero */}
      <div className="rounded-xl px-4 py-3.5 mb-4 flex items-baseline gap-3 flex-wrap"
        style={{ background: `linear-gradient(135deg, ${COLORS.accentSoft}, rgba(201,160,76,.10))`, border: `1px solid ${withAlpha(GOLD, '55')}` }}>
        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: GOLD_DARK }}>{t('Coût matières vrai / album')}</span>
        <span className="text-[26px] font-bold tabular-nums" style={{ fontFamily: SERIF, color: COLORS.textPrimary }}>{fmtMad(costPerAlbum)}</span>
        <span className="text-[12px]" style={{ color: COLORS.textSecondary }}>{t('à {n} pages', { n: simPages })}</span>
      </div>

      <div className="space-y-2">
        {current.map((l, i) => {
          const m = matFor(l.material_id);
          const qtyTotal = (Number(l.qty_fixed) || 0) + (Number(l.qty_per_page) || 0) * simPages;
          return (
            <div key={l.material_id} className="rounded-xl p-3" style={{ border: `1px solid ${COLORS.borderSubtle}` }}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold min-w-[130px]" style={{ color: COLORS.textPrimary }}>{m?.name ?? '—'}</span>
                <label className="flex items-center gap-1.5 text-xs" style={{ color: COLORS.textSecondary }}>
                  {t('fixe')}
                  <input
                    className="w-20 px-2 py-1.5 border rounded-lg text-sm text-center" style={inputStyle}
                    type="number" min="0" step="0.01" value={l.qty_fixed}
                    onChange={(e) => setLines(current.map((x, j) => (j === i ? { ...x, qty_fixed: e.target.value } : x)))}
                  />
                </label>
                <label className="flex items-center gap-1.5 text-xs" style={{ color: COLORS.textSecondary }}>
                  {t('+ × pages')}
                  <input
                    className="w-24 px-2 py-1.5 border rounded-lg text-sm text-center" style={inputStyle}
                    type="number" min="0" step="0.001" value={l.qty_per_page}
                    onChange={(e) => setLines(current.map((x, j) => (j === i ? { ...x, qty_per_page: e.target.value } : x)))}
                  />
                </label>
                <span className="text-xs" style={{ color: COLORS.textTertiary }}>{m?.consume_unit}</span>
                <button
                  type="button"
                  onClick={() => setLines(current.filter((_, j) => j !== i))}
                  className="ml-auto text-sm px-1" style={{ color: COLORS.error }}
                  title={t('Retirer de la recette')}
                >✕</button>
              </div>
              {/* line breakdown — qty @ the simulated page count → line cost */}
              <div className="flex items-baseline justify-between mt-2 pt-2 text-[11px]" style={{ borderTop: `1px solid ${COLORS.borderSubtle}`, color: COLORS.textTertiary }}>
                <span>{fmtQty(qtyTotal)} {m?.consume_unit} × {fmtMad(m?.avg_cost ?? 0)}</span>
                <span className="font-bold tabular-nums" style={{ color: GOLD_DARK }}>{fmtMad(lineCost(l))}</span>
              </div>
            </div>
          );
        })}
        {current.length === 0 && (
          <p className="text-xs py-3 text-center" style={{ color: COLORS.textTertiary }}>{t('Aucune matière dans la recette — ajoutez un consommable d’album ci-dessous.')}</p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4">
        {available.length > 0 && (
          <select
            className="px-3 py-2.5 border rounded-lg text-xs bg-white" style={inputStyle}
            value=""
            onChange={(e) => { if (e.target.value) setLines([...current, { material_id: e.target.value, qty_fixed: '1', qty_per_page: '0' }]); }}
          >
            <option value="">+ {t('Ajouter un consommable…')}</option>
            {available.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        <label className="flex items-center gap-1.5 text-xs" style={{ color: COLORS.textSecondary }}>
          {t('Simulation pages :')}
          <input
            className="w-16 px-2 py-1.5 border rounded-lg text-sm text-center" style={inputStyle}
            type="number" min="1" value={simPages} onChange={(e) => setSimPages(Number(e.target.value) || BASE_PAGES)}
          />
        </label>
        <div className="ml-auto">
          <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty}>{t('Enregistrer la recette')}</Button>
        </div>
      </div>
      {save.isError && <p className="text-xs mt-2" style={{ color: COLORS.error }}>{(save.error as Error).message}</p>}
    </div>
  );
}

// ─── Profit line helper ──────────────────────────────────────────────────────
function ProfitRow({ label, value, badge, strong }: { label: string; value: string; badge?: React.ReactNode; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between text-xs">
      <span className="flex items-center gap-1.5" style={{ color: strong ? COLORS.textPrimary : COLORS.textSecondary, fontWeight: strong ? 700 : 400 }}>
        {label}{badge}
      </span>
      <span className={`tabular-nums ${strong ? 'font-bold' : 'font-semibold'}`} style={{ color: COLORS.textPrimary }}>{value}</span>
    </div>
  );
}

/** Compact per-order profitability card — shared with the order-detail page,
 *  also reachable from the Stock-page lookup. */
function OrderProfitBody({ p }: { p: OrderProfit }) {
  const { t } = useT();
  const positive = p.profit >= 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[11px]" style={{ color: COLORS.textTertiary }}>
          {p.shopify_order_name || `#${p.shopify_order_id}`} · {t('{n} album(s)', { n: p.albums })} · {t('{n} p.', { n: p.pages })}
        </span>
      </div>
      <ProfitRow label={t('Chiffre d’affaires')} value={fmtMad(p.revenue)} />
      <ProfitRow label={t('Intérieur ({u}/page)', { u: fmtMad(p.page_unit) })} value={fmtMad(p.interior_cost)} />
      <ProfitRow label={t('Couverture (vinyle)')} value={fmtMad(p.cover_cost)} />
      <ProfitRow label={t('Matières (cartons + colle)')} value={fmtMad(p.materials_cost)} />
      <ProfitRow label={t('Emballage')} value={fmtMad(p.packaging_cost)} />
      <ProfitRow
        label={t('Livraison')}
        value={fmtMad(p.delivery_cost)}
        badge={p.delivery_estimated
          ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: withAlpha(COLORS.warning, 0.15), color: COLORS.warning, border: `1px solid ${withAlpha(COLORS.warning, '55')}` }}>{t('estimée')}</span>
          : undefined}
      />
      {p.lead_tracked && p.lead_cost > 0 && (
        <ProfitRow
          label={t('Coût du lead (pub)')}
          value={fmtMad(p.lead_cost)}
          badge={<span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border bg-[rgba(99,102,241,.12)] text-[#6366f1] border-[#6366f155] dark:bg-indigo-500/15 dark:text-[#A5B4FC] dark:border-indigo-400/40">{t('suivi')}</span>}
        />
      )}
      {p.redo_cost > 0 && <ProfitRow label={t('Réimpression / redo')} value={fmtMad(p.redo_cost)} />}
      <div className="pt-1.5 mt-1" style={{ borderTop: `1px solid ${COLORS.borderSubtle}` }}>
        <ProfitRow label={t('Coût total')} value={fmtMad(p.cost_total)} strong />
      </div>
      <div className="flex items-baseline justify-between text-sm rounded-lg px-2.5 py-2 mt-1"
        style={{ background: positive ? withAlpha(COLORS.success, 0.12) : withAlpha(COLORS.error, 0.10) }}>
        <span className="font-bold" style={{ color: positive ? COLORS.success : COLORS.error }}>{t('Bénéfice')}</span>
        <span className="tabular-nums font-bold" style={{ color: positive ? COLORS.success : COLORS.error }}>{fmtMad(p.profit)}</span>
      </div>
    </div>
  );
}

/** Stock-page lookup — enter an order number → see the profit breakdown.
 *  (The primary per-order view lives on the order-detail page; this is a quick
 *  cross-order check from the stock screen.) */
function OrderProfitLookup() {
  const { t } = useT();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState<string | null>(null);
  const { data, isFetching, error } = useQuery({
    queryKey: ['stock-order-profit', query],
    queryFn: () => stockApi.orderProfit(query!),
    enabled: !!query,
    retry: false,
  });
  return (
    <div className="rounded-2xl border p-5" style={{ background: COLORS.bgCard, borderColor: COLORS.borderSubtle, boxShadow: '0 2px 8px rgba(46,36,24,0.05)' }}>
      <SectionHead
        title={t('Rentabilité par commande')}
        subtitle={t('Bénéfice estimé d’une commande : CA − matières − emballage − livraison − redo.')}
      />
      <div className="flex gap-2 mb-3">
        <input
          className={inputCls} style={inputStyle}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && input.trim()) setQuery(input.trim().replace(/^#/, '')); }}
          placeholder={t('N° de commande Shopify (ex. 1076)')}
        />
        <Button size="sm" onClick={() => input.trim() && setQuery(input.trim().replace(/^#/, ''))} loading={isFetching} disabled={!input.trim()}>
          {t('Calculer')}
        </Button>
      </div>
      {error && <p className="text-xs" style={{ color: COLORS.error }}>{t('Commande introuvable.')}</p>}
      {data?.order_profit && <OrderProfitBody p={data.order_profit} />}
      {!query && <p className="text-xs" style={{ color: COLORS.textTertiary }}>{t('Entrez un numéro pour voir la marge. Le détail complet figure aussi sur la fiche commande.')}</p>}
    </div>
  );
}

// ─── Reusable material card-row ───────────────────────────────────────────────
// Mobile-first; reads as a card on phones, sits in a table-like grid on desktop.
// Top-level (not nested in the page) so React keeps each row mounted across
// re-renders — avoids focus/transition loss. The page passes `albums` (precomputed
// from the saved recipe) and the modal-opener callbacks.
function MaterialRow({ m, albums, onAdjust, onEdit }: {
  m: Material;
  albums: number | null;
  onAdjust: (m: Material) => void;
  onEdit: (m: Material) => void;
}) {
  const { t } = useT();
  const low = m.min_qty != null && m.stock_qty <= m.min_qty;
  const pct = m.min_qty != null
    ? Math.min(100, Math.round((m.stock_qty / (Number(m.max_qty) || Number(m.min_qty) * 3 || 1)) * 100))
    : null;
  return (
    <div
      className="rounded-xl p-3.5 transition-colors"
      style={{ border: `1px solid ${low ? `${withAlpha(COLORS.error, '55')}` : COLORS.borderSubtle}`, background: low ? withAlpha(COLORS.error, 0.04) : COLORS.bgCard }}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold truncate" style={{ color: COLORS.textPrimary }}>{m.name}</p>
            <ClassTag value={classOf(m)} />
          </div>
          <p className="text-[11px] mt-0.5" style={{ color: COLORS.textTertiary }}>
            {m.category === 'fourniture' ? t('Fourniture') : t('Consommable')}
            {' · '}{fmtMad(m.avg_cost)} / {m.consume_unit}
            {m.notes ? ` · ${m.notes}` : ''}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-bold tabular-nums leading-tight" style={{ color: low ? COLORS.error : COLORS.textPrimary }}>
            {fmtQty(m.stock_qty)} <span className="font-normal text-[11px]" style={{ color: COLORS.textTertiary }}>{m.consume_unit}</span>
          </p>
          {albums != null && (
            <p className="text-[10px] font-semibold mt-0.5" style={{ color: albums <= 3 ? COLORS.error : COLORS.textSecondary }}>
              ≈ {t('{n} album(s)', { n: albums })}
            </p>
          )}
        </div>
      </div>
      {pct != null && (
        <div className="h-1.5 mt-2 rounded-full overflow-hidden" style={{ background: COLORS.bgElevated }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: low ? COLORS.error : GOLD }} />
        </div>
      )}
      {low && <p className="text-[10px] font-semibold mt-1" style={{ color: COLORS.error }}>⚠ {t('sous le min ({qty} {unit})', { qty: fmtQty(Number(m.min_qty)), unit: m.consume_unit })}</p>}
      <div className="flex gap-2 mt-3">
        <button
          type="button" onClick={() => onAdjust(m)}
          className="flex-1 text-xs font-semibold py-2 rounded-lg min-h-[44px]"
          style={{ border: `1px solid ${COLORS.borderSubtle}`, color: COLORS.textSecondary }}
        >± {t('Ajuster')}</button>
        <button
          type="button" onClick={() => onEdit(m)}
          className="flex-1 text-xs font-semibold py-2 rounded-lg min-h-[44px]"
          style={{ background: COLORS.accentSoft, border: `1px solid ${withAlpha(GOLD, '55')}`, color: GOLD_DARK }}
        >{t('Modifier')}</button>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function AdminStockPage() {
  const { t } = useT();
  useEffect(() => { document.title = 'Stock — Admin Storiz'; }, []);
  const { data: matData } = useQuery({ queryKey: ['stock-materials'], queryFn: () => stockApi.materials() });
  const { data: alertData } = useQuery({ queryKey: ['stock-alerts'], queryFn: () => stockApi.alerts() });
  const { data: bomData } = useQuery({ queryKey: ['stock-bom'], queryFn: () => stockApi.bom() });
  const { data: mvData } = useQuery({ queryKey: ['stock-movements'], queryFn: () => stockApi.movements() });
  const { data: purchData } = useQuery({ queryKey: ['stock-purchases'], queryFn: () => stockApi.purchases() });

  const materials = matData?.materials ?? [];
  const alerts = alertData?.alerts ?? [];
  const queryClient = useQueryClient();
  const cancelPurchase = useMutation({
    mutationFn: (id: string) => stockApi.cancelPurchase(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-materials'] });
      queryClient.invalidateQueries({ queryKey: ['stock-purchases'] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
    },
  });
  const [editMat, setEditMat] = useState<Material | null>(null);
  const [showNewMat, setShowNewMat] = useState(false);
  const [adjustMat, setAdjustMat] = useState<Material | null>(null);
  const [showPurchase, setShowPurchase] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);

  // "≈ X albums possibles" per material — stock ÷ consumption for a base-page
  // album, from the saved recipe.
  const albumsPossible = (m: Material): number | null => {
    const line = (bomData?.bom ?? []).find((l: BomLine) => l.material_id === m.id);
    if (!line) return null;
    const perAlbum = (Number(line.qty_fixed) || 0) + (Number(line.qty_per_page) || 0) * BASE_PAGES;
    if (perAlbum <= 0) return null;
    return Math.floor(m.stock_qty / perAlbum);
  };

  const stockValue = materials.reduce((s, m) => s + m.stock_qty * m.avg_cost, 0);
  const lowCount = materials.filter((m) => m.min_qty != null && m.stock_qty <= m.min_qty).length;
  const REASON_LABELS: Record<string, string> = { achat: t('Achat'), commande: t('Commande'), ajustement: t('Ajustement'), perte: t('Perte') };

  // Materials that have a découpe yield / cut spec → Transformation section.
  const cutMaterials = materials.filter((m) => m.units_per_purchase > 1 || m.piece_kind);

  return (
    <AdminLayout
      title={t('Stock')}
      subtitle={t('Atelier — matières premières, découpe, recette d’album & rentabilité')}
      actions={
        <>
          <Button size="sm" onClick={() => setShowReceipt(true)}>📷 {t('Scanner un ticket')}</Button>
          <Button variant="ghost" size="sm" onClick={() => setShowNewMat(true)}>+ {t('Article')}</Button>
          <Button variant="ghost" size="sm" onClick={() => setShowPurchase(true)} disabled={materials.length === 0}>+ {t('Nouvel achat')}</Button>
        </>
      }
    >
      <div className="max-w-6xl space-y-6" style={{ color: COLORS.textPrimary }}>

        {/* ── Snapshot strip ── */}
        <div className="adm-rise grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { label: t('Valeur du stock'), value: fmtMad(stockValue), accent: false },
            { label: t('Articles suivis'), value: String(materials.length), accent: false },
            { label: t('Stock bas'), value: String(lowCount), accent: lowCount > 0 },
          ].map((s) => (
            <div key={s.label} className="rounded-xl px-4 py-3"
              style={{ background: s.accent ? withAlpha(COLORS.error, 0.06) : COLORS.bgCard, border: `1px solid ${s.accent ? `${withAlpha(COLORS.error, '44')}` : COLORS.borderSubtle}` }}>
              <p className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: s.accent ? COLORS.error : COLORS.textTertiary }}>{s.label}</p>
              <p className="text-[22px] font-bold tabular-nums leading-tight mt-0.5" style={{ fontFamily: SERIF, color: s.accent ? COLORS.error : COLORS.textPrimary }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* ── Low-stock alerts ── */}
        {alerts.length > 0 && (
          <div className="adm-rise rounded-xl border px-4 py-3" style={{ background: withAlpha(COLORS.error, 0.05), borderColor: `${withAlpha(COLORS.error, '4D')}` }}>
            <p className="text-sm font-bold mb-1.5" style={{ color: COLORS.error }}>⚠ {t('Stock bas — à racheter')}</p>
            <div className="flex flex-wrap gap-2">
              {alerts.map((a) => (
                <span key={a.id} className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: COLORS.bgCard, border: `1px solid ${withAlpha(COLORS.error, '40')}`, color: COLORS.error }}>
                  {a.name} : {fmtQty(Number(a.stock_qty))} / {t('min')} {fmtQty(Number(a.min_qty))} {a.consume_unit}
                  {a.lead_time_days ? ` · ${t('délai {n} j', { n: a.lead_time_days })}` : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── SECTION 1 · Matières premières ── */}
        <section className="adm-rise" style={{ animationDelay: '40ms' }}>
          <div className="rounded-2xl border p-5" style={{ background: COLORS.bgCard, borderColor: COLORS.borderSubtle, boxShadow: '0 2px 8px rgba(46,36,24,0.05)' }}>
            <SectionHead
              title={t('Matières premières')}
              subtitle={t('Tout ce que l’atelier garde en stock — matières d’album et fournitures.')}
              badge={lowCount > 0
                ? <span className="text-[11px] font-bold px-2.5 py-1 rounded-full self-center" style={{ background: withAlpha(COLORS.error, 0.10), color: COLORS.error, border: `1px solid ${withAlpha(COLORS.error, '44')}` }}>{t('{n} bas', { n: lowCount })}</span>
                : undefined}
              action={<Button variant="ghost" size="sm" onClick={() => setShowNewMat(true)}>+ {t('Article')}</Button>}
            />
            {materials.length === 0 ? (
              <p className="text-sm text-center py-8" style={{ color: COLORS.textTertiary }}>{t('Aucun article — créez le premier avec « + Article ».')}</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {materials.map((m) => (
                  <MaterialRow key={m.id} m={m} albums={albumsPossible(m)} onAdjust={setAdjustMat} onEdit={setEditMat} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ── SECTION 2 · Transformation & découpe ── */}
        <section className="adm-rise" style={{ animationDelay: '80ms' }}>
          <div className="rounded-2xl border p-5" style={{ background: COLORS.bgCard, borderColor: COLORS.borderSubtle, boxShadow: '0 2px 8px rgba(46,36,24,0.05)' }}>
            <SectionHead
              title={t('Transformation & découpe')}
              subtitle={t('Économie de la découpe : ce qu’on achète → ce qu’on en tire → le coût par unité consommée. (La découpe se modifie dans la fiche article.)')}
            />
            {cutMaterials.length === 0 ? (
              <p className="text-sm text-center py-6" style={{ color: COLORS.textTertiary }}>{t('Aucune matière avec découpe — ajoutez le rendement dans la fiche d’un article.')}</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {cutMaterials.map((m) => {
                  const purchaseCost = m.avg_cost * m.units_per_purchase;
                  const cutDim = m.piece_kind === 'custom'
                    ? (m.piece_w_mm && m.piece_h_mm ? `${m.piece_w_mm}×${m.piece_h_mm} mm` : null)
                    : (m.piece_kind && PIECE_PRESETS[m.piece_kind] ? t(PIECE_PRESETS[m.piece_kind].label) : null);
                  const sheetDim = m.purchase_w_mm && m.purchase_h_mm ? `${m.purchase_w_mm}×${m.purchase_h_mm} mm` : null;
                  return (
                    <div key={m.id} className="rounded-xl p-3.5" style={{ border: `1px solid ${COLORS.borderSubtle}` }}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold" style={{ color: COLORS.textPrimary }}>{m.name}</p>
                        <ClassTag value={classOf(m)} />
                        <button
                          type="button" onClick={() => setEditMat(m)}
                          className="ml-auto text-[11px] font-semibold px-2 py-1 rounded-lg"
                          style={{ color: GOLD_DARK, border: `1px solid ${withAlpha(GOLD, '44')}` }}
                        >{t('Modifier la découpe')}</button>
                      </div>
                      {/* Yield chain */}
                      <div className="flex items-center gap-2 mt-3 text-sm">
                        <span className="px-2.5 py-1.5 rounded-lg font-semibold text-xs" style={{ background: COLORS.bgElevated, color: COLORS.textPrimary }}>
                          1 {m.purchase_unit}{sheetDim ? ` · ${sheetDim}` : ''}
                        </span>
                        <span style={{ color: GOLD }}>→</span>
                        <span className="px-2.5 py-1.5 rounded-lg font-bold text-xs" style={{ background: COLORS.accentSoft, color: GOLD_DARK }}>
                          {fmtQty(m.units_per_purchase)} {m.consume_unit}
                        </span>
                      </div>
                      {cutDim && <p className="text-[11px] mt-2" style={{ color: COLORS.textTertiary }}>{t('Pièce :')} {cutDim}</p>}
                      {/* Cost economics */}
                      <div className="grid grid-cols-2 gap-2 mt-3 pt-3" style={{ borderTop: `1px solid ${COLORS.borderSubtle}` }}>
                        <div>
                          <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: COLORS.textTertiary }}>{t('Coût / {unit}', { unit: m.purchase_unit })}</p>
                          <p className="text-sm font-bold tabular-nums" style={{ color: COLORS.textPrimary }}>{fmtMad(purchaseCost)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: COLORS.textTertiary }}>{t('Coût / {unit}', { unit: m.consume_unit })}</p>
                          <p className="text-sm font-bold tabular-nums" style={{ color: GOLD_DARK }}>{fmtMad(m.avg_cost)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* ── SECTION 3 · Recette album ── */}
        <section className="adm-rise" style={{ animationDelay: '120ms' }}>
          <RecipeCard materials={materials} />
        </section>

        {/* ── Rentabilité par commande (lookup; primary view on order detail) ── */}
        <section className="adm-rise" style={{ animationDelay: '160ms' }}>
          <OrderProfitLookup />
        </section>

        {/* ── Achats + mouvements ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="adm-rise rounded-2xl border p-5" style={{ background: COLORS.bgCard, borderColor: COLORS.borderSubtle, boxShadow: '0 2px 8px rgba(46,36,24,0.05)', animationDelay: '200ms' }}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[16px] font-semibold" style={{ fontFamily: SERIF, color: COLORS.textPrimary }}>🧾 {t('Achats récents')}</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowPurchase(true)}>+ {t('Achat')}</Button>
            </div>
            <div className="space-y-2">
              {(purchData?.purchases ?? []).slice(0, 8).map((p) => (
                <div key={p.id} className="rounded-lg px-3 py-2.5 group" style={{ border: `1px solid ${COLORS.borderSubtle}` }}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold" style={{ color: COLORS.textSecondary }}>{p.supplier || t('Achat')} · {p.purchase_date}</p>
                    <span className="flex items-center gap-2">
                      <p className="text-xs font-bold tabular-nums" style={{ color: GOLD_DARK }}>{p.total_amount != null ? fmtMad(Number(p.total_amount)) : '—'}</p>
                      <button
                        type="button"
                        title={t('Annuler cet achat (le stock est re-déduit)')}
                        onClick={() => { if (window.confirm(t('Annuler cet achat ? Les entrées de stock correspondantes seront retirées.'))) cancelPurchase.mutate(p.id); }}
                        className="text-[10px] font-bold opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: COLORS.error }}
                      >
                        ✕ {t('Annuler')}
                      </button>
                    </span>
                  </div>
                  <p className="text-[11px] mt-0.5" style={{ color: COLORS.textTertiary }}>
                    {(p.purchase_lines ?? []).map((l) => `${fmtQty(Number(l.qty_purchase_units))} ${l.materials?.purchase_unit ?? ''} ${l.materials?.name ?? ''}`).join(' · ')}
                  </p>
                </div>
              ))}
              {(purchData?.purchases ?? []).length === 0 && (
                <p className="text-xs py-4 text-center" style={{ color: COLORS.textTertiary }}>{t('Aucun achat enregistré.')}</p>
              )}
            </div>
          </div>

          <div className="adm-rise rounded-2xl border p-5" style={{ background: COLORS.bgCard, borderColor: COLORS.borderSubtle, boxShadow: '0 2px 8px rgba(46,36,24,0.05)', animationDelay: '240ms' }}>
            <h2 className="text-[16px] font-semibold mb-3" style={{ fontFamily: SERIF, color: COLORS.textPrimary }}>🔄 {t('Derniers mouvements')}</h2>
            <div className="space-y-1.5">
              {(mvData?.movements ?? []).slice(0, 10).map((mv) => (
                <div key={mv.id} className="flex items-center gap-2 text-xs py-1.5" style={{ borderBottom: `1px solid ${COLORS.borderSubtle}` }}>
                  <span className="font-bold tabular-nums w-16 text-right" style={{ color: mv.qty >= 0 ? COLORS.success : COLORS.error }}>
                    {mv.qty >= 0 ? '+' : ''}{fmtQty(Number(mv.qty))}
                  </span>
                  <span className="font-medium truncate" style={{ color: COLORS.textSecondary }}>{mv.materials?.name}</span>
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold shrink-0" style={{ background: COLORS.bgElevated, color: COLORS.textTertiary, border: `1px solid ${COLORS.borderSubtle}` }}>
                    {REASON_LABELS[mv.reason] ?? mv.reason}{mv.ref_order_id ? ` · ${mv.ref_order_id.startsWith('1') || mv.ref_order_id.startsWith('7') ? '#' : ''}${mv.ref_order_id.slice(-4)}` : ''}
                  </span>
                  <span className="ml-auto shrink-0" style={{ color: COLORS.textTertiary }}>{new Date(mv.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</span>
                </div>
              ))}
              {(mvData?.movements ?? []).length === 0 && (
                <p className="text-xs py-4 text-center" style={{ color: COLORS.textTertiary }}>{t('Aucun mouvement.')}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showNewMat && <MaterialModal material={null} onClose={() => setShowNewMat(false)} />}
      {editMat && <MaterialModal material={editMat} onClose={() => setEditMat(null)} />}
      {adjustMat && <AdjustModal material={adjustMat} onClose={() => setAdjustMat(null)} />}
      {showPurchase && <PurchaseModal materials={materials} onClose={() => setShowPurchase(false)} />}
      {showReceipt && <ReceiptIntakeModal materials={materials} onClose={() => setShowReceipt(false)} />}
    </AdminLayout>
  );
}

// ─── Exported for reuse on the order-detail page ──────────────────────────────
export { OrderProfitBody };
